import React, { useRef, useEffect, useState } from "react";
import "./RegionOfInterestInfo.css";
import { BASE_URL } from "../../config";
import { showMapConfirmToast } from "../../utils/Toast/mapToast";

const RegionOfInterestInfo = ({
    regionStubovi = [],
    regionTrafostanice = [],
    regionVodovi = [],
    setOverlay,
}) => {
    const [checkedStubovi, setCheckedStubovi] = useState([]);
    const [checkedTrafostanice, setCheckedTrafostanice] = useState([]);
    const [checkedVodovi, setCheckedVodovi] = useState([]);

    const checkedStuboviRef = useRef([]);
    const checkedTrafostaniceRef = useRef([]);
    const checkedVodoviRef = useRef([]);

    useEffect(() => {
        checkedStuboviRef.current = checkedStubovi;
        checkedTrafostaniceRef.current = checkedTrafostanice;
        checkedVodoviRef.current = checkedVodovi;
    }, [checkedStubovi, checkedTrafostanice, checkedVodovi]);

    const getVodId = (vod) => vod?.properties?.id_vod;

    const getVodLabel = (vod) =>
        vod?.properties?.naziv_voda ??
        vod?.properties?.naziv ??
        vod?.properties?.pripadnost_vodu ??
        vod?.properties?.id_vod ??
        "Bez naziva";

    const getStubId = (stub) => stub?.properties?.id_stubnog_mjesta;

    const getStubLabel = (stub) =>
        stub?.properties?.id_stubnog_mjesta ?? "Bez naziva";

    const getTrafostanicaId = (ts) => ts?.properties?.id_trafo_stanice;

    const getTrafostanicaLabel = (ts) =>
        ts?.properties?.naziv ??
        ts?.properties?.naziv_trafo_stanice ??
        ts?.properties?.id_trafo_stanice ??
        "Bez naziva";

    const removeDuplicates = (items, getId) => {
        const keys = new Set();

        return items.filter((item) => {
            const id = getId(item);
            if (id === undefined || id === null || id === "") return false;

            const key = String(id);
            if (keys.has(key)) return false;

            keys.add(key);
            return true;
        });
    };

    const cleanVodovi = removeDuplicates(regionVodovi, getVodId);
    const cleanStubovi = removeDuplicates(regionStubovi, getStubId);
    const cleanTrafostanice = removeDuplicates(regionTrafostanice, getTrafostanicaId);

    const selectedCount =
        checkedVodovi.length + checkedStubovi.length + checkedTrafostanice.length;

    const totalCount =
        cleanVodovi.length + cleanStubovi.length + cleanTrafostanice.length;

    const handleCheckboxChange = (id, setFn) => {
        if (id === undefined || id === null || id === "") return;

        setFn((prev) =>
            prev.some((item) => String(item) === String(id))
                ? prev.filter((item) => String(item) !== String(id))
                : [...prev, id]
        );
    };

    const clearAllSelection = () => {
        setCheckedVodovi([]);
        setCheckedStubovi([]);
        setCheckedTrafostanice([]);
    };

    const getSelectedIdsByType = (type) => {
        if (type === "vodovi") return checkedVodoviRef.current;
        if (type === "stubovi") return checkedStuboviRef.current;
        if (type === "trafostanice") return checkedTrafostaniceRef.current;
        return [];
    };

    const resetCheckedByType = (type) => {
        if (type === "vodovi") setCheckedVodovi([]);
        if (type === "stubovi") setCheckedStubovi([]);
        if (type === "trafostanice") setCheckedTrafostanice([]);
    };

    const handleReport = async (type) => {
        const ids = getSelectedIdsByType(type);

        if (!ids.length) {
            showMapConfirmToast({
                title: "Nema odabranih objekata",
                message: "Prvo označite najmanje jedan objekat za izvještaj.",
                confirmText: "U redu",
                cancelText: "",
                variant: "warning",
                autoClose: 3000,
                showTimer: true,
            });
            return;
        }

        const urlMap = {
            vodovi: `${BASE_URL}/api/generate-vodovi-report`,
            stubovi: `${BASE_URL}/api/generate-stubovi-report`,
            trafostanice: `${BASE_URL}/api/generate-trafostanice-report`,
        };

        const labelMap = {
            vodovi: "vodove",
            stubovi: "stubove",
            trafostanice: "trafostanice",
        };

        try {
            setOverlay?.({
                active: true,
                message: "Generišem izvještaj, molimo sačekajte...",
            });

            const response = await fetch(urlMap[type], {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ [`${type}_ids`]: ids }),
            });

            if (!response.ok) {
                showMapConfirmToast({
                    title: "Greška",
                    message: `Greška pri generisanju izvještaja za ${labelMap[type]}.`,
                    confirmText: "U redu",
                    cancelText: "",
                    variant: "danger",
                    autoClose: 4000,
                    showTimer: true,
                });
                return;
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = `revizija_${type}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(downloadUrl);
            resetCheckedByType(type);

            showMapConfirmToast({
                title: "Izvještaj generisan",
                message: `Uspješno generisan izvještaj za ${labelMap[type]}.`,
                confirmText: "U redu",
                cancelText: "",
                variant: "success",
                autoClose: 3000,
                showTimer: true,
            });
        } catch (err) {
            console.error("Greška:", err);

            showMapConfirmToast({
                title: "Greška servera",
                message: "Došlo je do greške prilikom povezivanja sa serverom.",
                confirmText: "U redu",
                cancelText: "",
                variant: "danger",
                autoClose: 4000,
                showTimer: true,
            });
        } finally {
            setOverlay?.({ active: false, message: "" });
        }
    };

    const renderSection = ({
        title,
        subtitle,
        items,
        type,
        checkedItems,
        setCheckedItems,
        getId,
        getLabel,
        buttonText,
    }) => {
        if (!Array.isArray(items) || items.length === 0) return null;

        return (
            <div className="roi-section">
                <div className="roi-section-header">
                    <div>
                        <h3 className="roi-section-title">{title}</h3>
                        <p className="roi-section-subtitle">{subtitle}</p>
                    </div>

                    <div className="roi-section-count">
                        {checkedItems.length} / {items.length}
                    </div>
                </div>

                <div className="roi-list">
                    {items.map((item, index) => {
                        const id = getId(item);
                        const label = getLabel(item);
                        const isChecked = checkedItems.some(
                            (checkedId) => String(checkedId) === String(id)
                        );

                        return (
                            <div
                                className={`roi-item ${isChecked ? "active" : ""}`}
                                key={`${type}-${String(id)}-${index}`}
                            >
                                <div className="roi-label-wrap">
                                    <span className="roi-label">{label}</span>
                                </div>

                                <label className="roi-switch">
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleCheckboxChange(id, setCheckedItems)}
                                    />
                                    <span className="roi-slider"></span>
                                </label>
                            </div>
                        );
                    })}
                </div>

                <button
                    type="button"
                    className="roi-report-button"
                    onClick={() => handleReport(type)}
                >
                    {buttonText}
                </button>
            </div>
        );
    };

    return (
        <div className="region-of-interest-info-div">
            <div className="roi-card">
                <div className="roi-header">
                    <div className="roi-title-wrap">
                        <h3 className="roi-title">Region od interesa</h3>
                        <p className="roi-subtitle">
                            Označi objekte iz regiona i kreiraj nalog za reviziju
                        </p>
                    </div>

                    <div className="roi-count">
                        {selectedCount} / {totalCount}
                    </div>
                </div>

                <div className="roi-toolbar">
                    <button
                        type="button"
                        className="roi-clear-btn"
                        onClick={clearAllSelection}
                        disabled={selectedCount === 0}
                    >
                        Poništi izbor
                    </button>
                </div>

                <div className="roi-content">
                    {renderSection({
                        title: "Vodovi",
                        subtitle: "Naziv voda",
                        items: cleanVodovi,
                        type: "vodovi",
                        checkedItems: checkedVodovi,
                        setCheckedItems: setCheckedVodovi,
                        getId: getVodId,
                        getLabel: getVodLabel,
                        buttonText: "Kreiraj izvještaj za vodove",
                    })}

                    {renderSection({
                        title: "Stubovi",
                        subtitle: "ID stubnog mjesta",
                        items: cleanStubovi,
                        type: "stubovi",
                        checkedItems: checkedStubovi,
                        setCheckedItems: setCheckedStubovi,
                        getId: getStubId,
                        getLabel: getStubLabel,
                        buttonText: "Kreiraj izvještaj za stubove",
                    })}

                    {renderSection({
                        title: "Trafostanice",
                        subtitle: "Naziv trafostanice",
                        items: cleanTrafostanice,
                        type: "trafostanice",
                        checkedItems: checkedTrafostanice,
                        setCheckedItems: setCheckedTrafostanice,
                        getId: getTrafostanicaId,
                        getLabel: getTrafostanicaLabel,
                        buttonText: "Kreiraj izvještaj za trafostanice",
                    })}

                    {totalCount === 0 && (
                        <div className="roi-empty">
                            Nema pronađenih objekata u regionu od interesa.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegionOfInterestInfo;